/**
 * Transport Factory
 *
 * Creates transport instances based on configuration type.
 * Provides auto-detection and selection logic.
 */

import type { ITransport, TransportConfig, TransportType } from '../types';
import { SignalRTransport } from './SignalRTransport';
import { HttpPollingTransport } from './HttpPollingTransport';
import { SseTransport } from './SseTransport';
import { RawWebSocketTransport } from './RawWebSocketTransport';
import { generatePrefixedId } from '../utils';

/**
 * Create transport instance from configuration
 *
 * @param config - Transport configuration
 * @returns Transport instance
 * @throws Error if transport type unknown
 */
export function createTransport(config: TransportConfig): ITransport {
  const id = generatePrefixedId('transport');

  switch (config.type) {
    case 'signalr':
      return new SignalRTransport(id, config);

    case 'http-polling':
      return new HttpPollingTransport(id, config);

    case 'sse':
      return new SseTransport(id, config);

    case 'websocket':
      return new RawWebSocketTransport(id, config);

    default:
      throw new Error(`Unknown transport type: ${(config as any).type}`);
  }
}

/**
 * Detect best transport for environment
 *
 * Strategy:
 * 1. WebSocket if browser supports it
 * 2. SSE if supported
 * 3. HTTP Polling fallback (always available)
 *
 * @returns Transport type
 */
export function detectBestTransport(): TransportType {
  // Check WebSocket support
  if (typeof WebSocket !== 'undefined') {
    return 'websocket';
  }

  // Check EventSource (SSE) support
  if (typeof EventSource !== 'undefined') {
    return 'sse';
  }

  // Fallback to HTTP Polling
  return 'http-polling';
}

/**
 * Create transport with auto-detection
 *
 * @param url - Server URL
 * @param preferredType - Preferred transport type (optional)
 * @returns Transport instance
 */
export function createAutoTransport(url: string, preferredType?: TransportType): ITransport {
  const type = preferredType || detectBestTransport();

  return createTransport({
    type,
    url,
    reconnect: {
      maxAttempts: 0,
      initialDelayMs: 1000,
      maxDelayMs: 30000,
      backoffMultiplier: 2,
    },
  });
}
